import React, { useCallback, useRef, useState } from 'react';
import './BFFilterSearch.css';

// ============ TYPES ============

export type BFFilterSearchSize = 'sm' | 'md' | 'lg';

export interface BFFilterSearchProps {
    /** Current search value */
    value?: string;
    /** Fires on every keystroke */
    onChange?: (value: string) => void;
    /** Fires when the clear button is pressed */
    onClear?: () => void;
    /** Placeholder shown in the empty / unfocused state */
    placeholder?: string;
    /**
     * Label prefix shown before the value when the input is blurred and has
     * a value (e.g. "ID/VAT" → displays "ID/VAT: *25560450*").
     */
    searchLabel?: string;
    /** CSS max-width of the control; text beyond this is truncated with "…" */
    maxWidth?: string | number;
    /** Whether the control is disabled */
    disabled?: boolean;
    /** Size variant */
    size?: BFFilterSearchSize;
    /** Additional CSS class */
    className?: string;
}

// ============ INTERNAL ICONS ============

function SearchIcon({ size = 16 }: { size?: number }) {
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
        >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
    );
}

function XIcon({ size = 10 }: { size?: number }) {
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
        >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
    );
}

// ============ COMPONENT ============

export function BFFilterSearch({
    value = '',
    onChange,
    onClear,
    placeholder = 'Search…',
    searchLabel,
    maxWidth,
    disabled = false,
    size = 'md',
    className = '',
}: BFFilterSearchProps) {
    const [isFocused, setIsFocused] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    const hasValue = value.length > 0;

    // Icon size mapping without nested ternary
    const iconSizeMap: Record<BFFilterSearchSize, number> = { sm: 13, md: 15, lg: 18 };
    const iconSize = iconSizeMap[size];

    // When the styled overlay is clicked, forward focus to the real input
    const handleOverlayClick = useCallback(() => {
        if (!disabled) {
            inputRef.current?.focus();
        }
    }, [disabled]);

    const handleClear = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            onChange?.('');
            onClear?.();
            // Return focus to the input after clearing
            requestAnimationFrame(() => inputRef.current?.focus());
        },
        [onChange, onClear],
    );

    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            onChange?.(e.target.value);
        },
        [onChange],
    );

    // Overlay is visible when blurred and a value is present
    const showOverlay = !isFocused && hasValue;

    const containerClasses = [
        'bf-filter-search',
        `bf-filter-search--${size}`,
        hasValue ? 'bf-filter-search--active' : '',
        isFocused ? 'bf-filter-search--focused' : '',
        disabled ? 'bf-filter-search--disabled' : '',
        className,
    ]
        .filter(Boolean)
        .join(' ');

    let maxWidthStyle: React.CSSProperties | undefined;
    if (maxWidth != null) {
        const resolvedWidth = typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth;
        // Set both width and max-width so the container is the same size
        // in edit mode (real input) and display mode (overlay), preventing
        // layout shifts when switching between the two.
        maxWidthStyle = { maxWidth: resolvedWidth, width: resolvedWidth };
    }

    return (
        <div className={containerClasses} style={maxWidthStyle}>
            {/* Left search icon */}
            <span className="bf-filter-search__icon">
                <SearchIcon size={iconSize} />
            </span>

            {/* Real input – always mounted so focus/blur events work */}
            <input
                ref={inputRef}
                type="text"
                className={['bf-filter-search__input', showOverlay ? 'bf-filter-search__input--hidden' : '']
                    .filter(Boolean)
                    .join(' ')}
                value={value}
                placeholder={placeholder}
                disabled={disabled}
                onChange={handleChange}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                aria-label={searchLabel ?? placeholder}
            />

            {/* Formatted display overlay – shown when blurred + has value */}
            {showOverlay && (
                <span className="bf-filter-search__overlay" onClick={handleOverlayClick} aria-hidden="true">
                    {searchLabel && <span className="bf-filter-search__overlay-label">{searchLabel}:&nbsp;</span>}
                    <span className="bf-filter-search__overlay-value">{value}</span>
                </span>
            )}

            {/* Clear button – only when value is present */}
            {hasValue && (
                <button
                    type="button"
                    className="bf-filter-search__clear"
                    onClick={handleClear}
                    aria-label="Clear search"
                    tabIndex={0}
                    disabled={disabled}
                >
                    <XIcon size={size === 'lg' ? 12 : 10} />
                </button>
            )}
        </div>
    );
}

export default BFFilterSearch;
